import { isApiError } from "@core/api/api-error";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { PatientProgramEditor } from "@domain/program/patient-program";
import { TemplateItem } from "@domain/template/template";
import styled from "@emotion/styled";
import { BaseDialog, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { NetworkError } from "@ui/components/common/error";
import { LoadingContainer } from "@ui/components/common/loading-container";
import { TemplateCard } from "@ui/components/program/program-card";
import { useTemplates } from "@ui/pages/template-builder/hooks/use-templates";
import { physioMeMobileBreakpoint, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { DialogProps } from "primereact/dialog";
import React, { useState } from "react";

export interface TemplateListingDialogProps extends DialogProps {
	onTemplateClicked: (template: TemplateItem) => Promise<void>;
	editor: PatientProgramEditor;
}

export const TemplateListingDialog: React.FC<TemplateListingDialogProps> = ({
	onTemplateClicked,
	editor,
	...otherProps
}) => {
	const { format } = useI18n();

	const handleTemplateClicked = async (template: TemplateItem) => {
		await onTemplateClicked(template);
		otherProps.onHide();
	};

	return (
		<BaseDialog header={format("templateListingDialog.title")} width={DIALOG_SIZE.S} {...otherProps}>
			<DialogContainer>
				<DialogContent onTemplateClicked={handleTemplateClicked} editor={editor} />
			</DialogContainer>
		</BaseDialog>
	);
};

const DialogContent: React.FC<{
	onTemplateClicked: (template: TemplateItem) => Promise<void>;
	editor: PatientProgramEditor;
}> = ({ onTemplateClicked, editor }) => {
	const { templates, loading, error, refreshTemplates } = useTemplates();
	const { format } = useI18n();
	const [clickError, setClickError] = useState(false);

	const [onTemplateClickedWithLoading, clickLoading] = useAsyncOperation(onTemplateClicked, { rethrow: true });

	if (!templates || clickLoading || clickError) {
		if (loading || clickLoading) {
			return (
				<CenteredContent>
					<LoadingContainer style={{ justifyContent: "center" }} />
				</CenteredContent>
			);
		}
		return (
			<CenteredContent>
				<NetworkError
					titleStyle={{ fontSize: 24 }}
					description={isApiError(error) || clickError ? format("templateListingDialog.error") : undefined}
					onRefresh={clickError ? () => setClickError(false) : refreshTemplates}
				/>
			</CenteredContent>
		);
	}

	if (templates.length == 0) {
		return (
			<Container>
				<Title>{format("templateListingDialog.empty.title")}</Title>
				<Description>{format("templateListingDialog.empty.description")}</Description>
			</Container>
		);
	}

	return (
		<>
			{templates.map(template => (
				<TemplateCard
					key={template.id}
					editor={editor}
					template={template}
					onClick={async () => {
						try {
							setClickError(false);
							await onTemplateClickedWithLoading(template);
						} catch (e) {
							console.error(e);
							setClickError(true);
						}
					}}
				/>
			))}
		</>
	);
};

const DialogContainer = styled.div`
	${stack(24, "flexstart", "stretch")};
	min-height: 500px;

	@media ${physioMeMobileBreakpoint} {
		min-height: unset;
	}
`;

const Container = styled.div`
	flex: 1;
	${stack(16, "center", "center")}
`;

const Title = styled.h3`
	text-align: center;
	color: ${colors.grey};
`;

const Description = styled.div`
	text-align: center;
	max-width: 448px;
	color: ${colors.grey};
`;

const CenteredContent = styled.div`
	flex: 1;
	${stack(undefined, "center", "center")};
`;
